const post = {
  slug: 'youtube-rpm-by-niche',
  cluster: 'niches',
  eyebrow: 'Niches',
  title: 'What RPM actually means for niche choice',
  description:
    'Ad rates vary a lot by subject. Here is why that matters much less than it looks when you are choosing what to make.',
  keywords: [
    'youtube rpm', 'youtube cpm by niche', 'highest paying youtube niches',
    'youtube monetisation', 'youtube ad revenue', 'how much youtube pays',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-rpm-by-niche.webp',
    alt: 'Two bars: a tall rate multiplied by a tiny audience beside a modest rate multiplied by a large one.',
  },
  lede:
    'Ad rates genuinely do vary by a large factor between subjects. That fact is true, widely repeated, and close to useless as a way of picking a niche — for a reason that is just arithmetic.',
  sections: [
    {
      heading: 'The terms, briefly',
      body: [
        {
          type: 'list',
          items: [
            '**CPM** is what an advertiser pays per thousand ad impressions.',
            '**RPM** is what the *creator* receives per thousand video views, after the platform\'s share and after accounting for views that carried no ad at all.',
          ],
        },
        {
          type: 'para',
          text: 'RPM is the number that matters to you, and it is always substantially lower than the CPM figure quoted in articles. Anyone comparing niches using CPM is comparing the wrong number.',
        },
      ],
    },
    {
      heading: 'Why the rate is the wrong first filter',
      body: [
        {
          type: 'para',
          text: 'Revenue is rate multiplied by views. Rate varies between subjects by something like a single order of magnitude. Views vary between channels by several.',
        },
        {
          type: 'para',
          text: 'A channel in a high-rate subject with two thousand views a month earns less than a channel in a low-rate subject with two hundred thousand. The multiplier that dominates is not the one people optimise.',
        },
        {
          type: 'callout',
          title: 'The arithmetic to actually do',
          text: 'Before choosing a niche for its rate, estimate how many views you realistically expect there versus in the niche you actually know something about. If the second is more than a few times larger, the rate difference is irrelevant.',
        },
      ],
    },
    {
      heading: 'What genuinely moves the rate',
      body: [
        {
          type: 'para',
          text: 'Beyond subject, several factors matter and are less discussed.',
        },
        {
          type: 'list',
          items: [
            '**Where the audience is.** Viewers in some countries are worth considerably more to advertisers than others, and a topic with a globally distributed audience earns differently from one concentrated in a high-value market.',
            '**Season.** Advertiser spend is not flat across the year. The same video earns a different amount in different months.',
            '**Video length**, because longer videos can carry more ad breaks — though this cuts against retention if you pad, which loses more than it gains.',
            '**Advertiser-friendliness.** Subjects that trip content guidelines get fewer bidders regardless of how good the video is.',
          ],
        },
        {
          type: 'para',
          text: 'These interact, which is why published "average RPM by niche" tables should be treated as very rough. Two channels in the same subject routinely see quite different rates.',
        },
      ],
    },
    {
      heading: 'Ads are usually the smallest revenue line anyway',
      body: [
        {
          type: 'para',
          text: 'For most channels that make money, ad revenue is not the largest component. Sponsorship, affiliate arrangements and the creator\'s own products typically exceed it, often by a lot.',
        },
        {
          type: 'para',
          text: 'That changes the calculation, because those depend on having an audience that trusts a specific channel about a specific thing — which is an argument for a narrow, sustainable niche rather than a high-rate one. Sponsors buy relevance to their product, and a focused small audience is frequently worth more to them than a diffuse large one.',
        },
      ],
    },
    {
      heading: 'Where the rate should enter the decision',
      body: [
        {
          type: 'para',
          text: 'Last, as a tiebreak. If two niches both pass demand, sustainability and visual material, and one has materially better rates, take that one.',
        },
        {
          type: 'para',
          text: 'Using it as the first filter produces the failure described in [niches to avoid](/blogs/youtube-niches-to-avoid): a channel about a subject the creator has no interest in, competing against people who do, and abandoned at video nine with no revenue of any kind.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'What RPM should I expect?',
      a: 'It varies too widely by subject, audience location and season for a figure here to be useful, and any specific number would be one we could not defend across your circumstances. Your own analytics, once you are monetised, is the only reliable source.',
    },
    {
      q: 'Do faceless channels earn less than presenter-led ones?',
      a: 'Not inherently — advertisers buy the audience, not the presence of a face. Where faceless channels do lose ground is sponsorship, since some of that market pays for personal endorsement, which a narrator cannot offer in the same way.',
    },
  ],
  related: ['faceless-youtube-niches', 'youtube-niches-to-avoid', 'how-to-evaluate-a-youtube-niche'],
};

export default post;
